import { Suspense, useContext } from 'react';
import {
  Navigate,
  Route,
  Routes as RouterRoutes,
  useLocation,
} from 'react-router-dom';
import { AuthContext } from 'app/auth';
import { AzureInstallationDialog } from '../components/AzureInstallationDialog';
import {
  ReviewManifest as AzureReviewManifest,
  InstallGuide as AzureInstallGuide,
  ConnectRepositories as AzureConnectRepositories,
  CheckConnection as AzureCheckConnection,
  EnableQodo as AzureEnableQodo,
  ChooseMethod as AzureChooseMethod,
} from '../components/AzureInstallationDialog/steps';
import { BitbucketInstallationDialog } from '../components/BitbucketInstallationDialog';
import { OAuthCallbackReceiver } from '../components/BitbucketInstallationDialog/parts/OAuthCallbackReceiver';
import { OAuthRedirectPage } from '../components/BitbucketInstallationDialog/parts/OAuthRedirectPage';
import {
  BeforeYouBegin as BitbucketBeforeYouBegin,
  CheckConnection as BitbucketCheckConnection,
  CreateBotUser as BitbucketCreateBotUser,
  EnableQodo as BitbucketEnableQodo,
  InstallGuide as BitbucketInstallGuide,
  SelectRepositories as BitbucketSelectRepositories,
} from '../components/BitbucketInstallationDialog/steps';
import { GerritInstallationDialog } from '../components/GerritInstallationDialog';
import {
  Connect as GerritConnect,
  ImportProgress as GerritImportProgress,
  Prerequisites as GerritPrerequisites,
} from '../components/GerritInstallationDialog/steps';
import { GithubManifestInstallationDialog } from '../components/GithubManifestInstallationDialog';
import { CheckConnection } from '../components/GithubManifestInstallationDialog/steps/CheckConnection';
import { EnableQodo } from '../components/GithubManifestInstallationDialog/steps/EnableQodo';
import { InstallApp } from '../components/GithubManifestInstallationDialog/steps/InstallApp';
import { OrganizationDetails } from '../components/GithubManifestInstallationDialog/steps/OrganizationDetails';
import { RecommendedDisplay } from '../components/GithubManifestInstallationDialog/steps/RecommendedDisplay';
import { ReviewManifest } from '../components/GithubManifestInstallationDialog/steps/ReviewManifest';
import { GithubPublicAppInstallationDialog } from '../components/GithubPublicAppInstallationDialog';
import { InstallationComplete as MTInstallationComplete } from '../components/GithubPublicAppInstallationDialog/steps/InstallationComplete';
import { InstallGuide as MTInstallGuide } from '../components/GithubPublicAppInstallationDialog/steps/InstallGuide';
import { GitlabInstallationDialog } from '../components/GitlabInstallationDialog';
import { GitlabMTInstallationDialog } from '../components/GitlabInstallationDialog/GItlabMTInstallationDialog';
import { BeforeYouBegin as GitlabBeforeYouBegin } from '../components/GitlabInstallationDialog/steps/BeforeYouBegin';
import { CheckConnection as GitlabCheckConnection } from '../components/GitlabInstallationDialog/steps/CheckConnection';
import { ConfigureApp as GitlabConfigureApp } from '../components/GitlabInstallationDialog/steps/ConfigureApp';
import { ConfigureMTApp as GitlabConfigureMTApp } from '../components/GitlabInstallationDialog/steps/ConfigureMTApp';
import { ConfigureTokenStep as GitlabConfigureToken } from '../components/GitlabInstallationDialog/steps/ConfigureTokenStep';
import { ConnectRepositories as GitlabConnectRepositories } from '../components/GitlabInstallationDialog/steps/ConnectRepositories';
import { CreateAppGuide as GitlabCreateAppGuide } from '../components/GitlabInstallationDialog/steps/CreateAppGuide';
import { CreateServiceAccountGuide as GitlabCreateServiceAccountGuide } from '../components/GitlabInstallationDialog/steps/CreateServiceAccountGuide';
import { EnableQodo as GitlabEnableQodo } from '../components/GitlabInstallationDialog/steps/EnableQodo';
import { GitlabInstallationDialogV2 } from '../components/GitlabInstallationDialogV2';
import { Connect as GitlabV2Connect } from '../components/GitlabInstallationDialogV2/steps/Connect';
import { InstallResults as GitlabV2InstallResults } from '../components/GitlabInstallationDialogV2/steps/InstallResults';
import { SelectScope as GitlabV2SelectScope } from '../components/GitlabInstallationDialogV2/steps/SelectScope';
import { Loader } from '../components/Loader';
import { SuccessPage } from '../components/SuccessPage';
import { FEATURE_FLAGS } from '../consts/features';
import { usePageViewTracking } from '../hooks/usePageViewTracking';
import { useUser } from '../hooks/useUser';
import { Main as MainLayout } from '../layouts/Main';
import { OnboardingWizard as OnboardingWizardLayout } from '../layouts/OnboardingWizard';
import { Error } from '../pages/Error';
import { ERROR_TYPES } from '../pages/Error/consts';
import { Login } from '../pages/Login';
import { CheckYourEmail } from '../pages/Login/parts/CheckYourEmail';
import { Email } from '../pages/Login/parts/Email';
import { SigninMethods } from '../pages/Login/parts/SigninMethods';
import { SSO } from '../pages/Login/parts/SSO';
import { isMtWizardEnabled, useDeploymentConfig } from '../services/config';
import { mixpanelService } from '../services/mixpanel';
import { identifySentryUser } from '../services/sentry/sentry';
import { SidebarProvider } from '../ui/sidebar';
import { isAirgappedMode } from '../utils/env';
import { PARAMS, ROUTE_PATHS, ROUTES } from './consts';
import { TeamProfilesRouteFallback } from './parts/TeamProfilesRouteFallback';
import { AnalyticsProtectedRoute } from './protectedRoutes/AnalyticsProtectedRoute';
import { BillingProtectedRoute } from './protectedRoutes/BillingProtectedRoute';
import { FeatureFlagProtectedRoute } from './protectedRoutes/FeatureFlagProtectedRoute';
import { GithubMarketplaceWizardProtectedRoute } from './protectedRoutes/GithubMarketplaceWizardProtectedRoute';
import { InstallationWizardProtectedRoute } from './protectedRoutes/InstallationWizardProtectedRoute';
import { LoginProtectedRoute } from './protectedRoutes/LoginProtectedRoute';
import { ManageSubscriptionProtectedRoute } from './protectedRoutes/ManageSubscriptionProtectedRoute';
import { OnboardingWizardProtectedRoute } from './protectedRoutes/OnboardingWizardProtectedRoute';
import { PersonasProtectedRoute } from './protectedRoutes/PersonasProtectedRoute';
import { PlansProtectedRoute } from './protectedRoutes/PlansProtectedRoute';
import { RootProtectedRoute } from './protectedRoutes/RootProtectedRoute';
import { TeamsProtectedRoute } from './protectedRoutes/TeamsProtectedRoute';
import { WikiCapabilityProtectedRoute } from './protectedRoutes/WikiCapabilityProtectedRoute';
import { lazyWithReload } from './utils';

const AnalyticsTab = lazyWithReload(() =>
  import('../components/Billing/parts/AnalyticsTab').then(
    ({ AnalyticsTab }) => ({ default: AnalyticsTab }),
  ),
);
const TransactionHistoryTab = lazyWithReload(() =>
  import('../components/Billing/parts/TransactionHistoryTab').then(
    ({ TransactionHistoryTab }) => ({ default: TransactionHistoryTab }),
  ),
);
const ManageSubscriptionTab = lazyWithReload(() =>
  import('../components/Billing/parts/ManageSubscriptionTab').then(
    ({ ManageSubscriptionTab }) => ({ default: ManageSubscriptionTab }),
  ),
);
const Wiki = lazyWithReload(() =>
  import('../pages/Wiki').then(({ Wiki }) => ({ default: Wiki })),
);
const WikiRepositoryView = lazyWithReload(() =>
  import('../pages/Wiki').then(({ WikiRepositoryView }) => ({
    default: WikiRepositoryView,
  })),
);
const FindingSidePanel = lazyWithReload(() =>
  import('../components/FindingSidePanel').then(({ FindingSidePanel }) => ({
    default: FindingSidePanel,
  })),
);
const LinkGitAccounts = lazyWithReload(() =>
  import('../components/LinkGitAccounts').then(({ LinkGitAccounts }) => ({
    default: LinkGitAccounts,
  })),
);
const RepositoriesSidePanel = lazyWithReload(() =>
  import('../components/RepositoriesSidePanel').then(
    ({ RepositoriesSidePanel }) => ({ default: RepositoriesSidePanel }),
  ),
);
const RuleSidePanel = lazyWithReload(() =>
  import('../components/RuleSidePanel').then(({ RuleSidePanel }) => ({
    default: RuleSidePanel,
  })),
);
const SkillSidePanel = lazyWithReload(() =>
  import('../components/SkillSidePanel').then(({ SkillSidePanel }) => ({
    default: SkillSidePanel,
  })),
);
const UserSidePanel = lazyWithReload(() =>
  import('../components/UserSidePanel').then(({ UserSidePanel }) => ({
    default: UserSidePanel,
  })),
);
const AnalyticsPage = lazyWithReload(() =>
  import('../pages/Analytics').then(({ Analytics }) => ({
    default: Analytics,
  })),
);
const ApiKeys = lazyWithReload(() =>
  import('../pages/ApiKeys').then(({ ApiKeys }) => ({ default: ApiKeys })),
);
const SandboxConfiguration = lazyWithReload(() =>
  import('../pages/SandboxConfiguration').then(({ SandboxConfiguration }) => ({
    default: SandboxConfiguration,
  })),
);
const AwareSuccessPage = lazyWithReload(() =>
  import('../pages/Aware/AwareSuccessPage').then(({ AwareSuccessPage }) => ({
    default: AwareSuccessPage,
  })),
);
const Billing = lazyWithReload(() =>
  import('../pages/Billing').then(({ Billing }) => ({ default: Billing })),
);
const Findings = lazyWithReload(() =>
  import('../pages/Findings').then(({ Findings }) => ({ default: Findings })),
);
const Home = lazyWithReload(() =>
  import('../pages/Home').then(({ Home }) => ({ default: Home })),
);
const ConfigurationPage = lazyWithReload(() =>
  import('../pages/IndexingConfiguration/ConfigurationPage').then(
    ({ ConfigurationPage }) => ({ default: ConfigurationPage }),
  ),
);
const IndexingMonitor = lazyWithReload(() =>
  import('../pages/IndexingMonitor').then(({ IndexingMonitor }) => ({
    default: IndexingMonitor,
  })),
);
const IntegrationsPage = lazyWithReload(() =>
  import('../pages/Integrations').then(({ Integrations }) => ({
    default: Integrations,
  })),
);
const Organizations = lazyWithReload(() =>
  import('../pages/Organization').then(({ Organizations }) => ({
    default: Organizations,
  })),
);
const Plans = lazyWithReload(() =>
  import('../pages/Plans').then(({ Plans }) => ({ default: Plans })),
);
const Repositories = lazyWithReload(() =>
  import('../pages/Repositories').then(({ Repositories }) => ({
    default: Repositories,
  })),
);
const RulesWrapper = lazyWithReload(() =>
  import('../pages/Rules/RulesWrapper').then(({ RulesWrapper }) => ({
    default: RulesWrapper,
  })),
);
const Settings = lazyWithReload(() =>
  import('../pages/Settings').then(({ Settings }) => ({ default: Settings })),
);
const Teams = lazyWithReload(() =>
  import('../pages/Teams').then(({ Teams }) => ({ default: Teams })),
);
const TeamProfiles = lazyWithReload(() =>
  import('../pages/TeamProfiles').then(({ TeamProfiles }) => ({
    default: TeamProfiles,
  })),
);
const TeamProfileDetail = lazyWithReload(() =>
  import('../pages/TeamProfiles').then(({ TeamProfileDetail }) => ({
    default: TeamProfileDetail,
  })),
);

export const Routes = () => {
  usePageViewTracking();
  // Read reactively so the bare-`/billing` checkout-return redirect preserves
  // the live `?checkout=…&session_id=…` query (PLT-3715).
  const { search: currentSearch } = useLocation();
  const { user: authUser } = useContext(AuthContext);
  const config = useDeploymentConfig();
  const isWizardUnrestricted = !isMtWizardEnabled(config);
  const {
    user,
    isAzureInstallationWizardAvailable,
    isAzureInstallationWizardLoading,
    isGitlabInstallationWizardAvailable,
    isGitlabInstallationWizardLoading,
    isGitlabWizardV2Enabled,
    isGitlabWizardV2Loading,
    isGitlabMTCloudInstallationWizardAvailable,
    isGitlabMTCloudInstallationWizardLoading,
    isBitbucketInstallationWizardAvailable,
    isBitbucketInstallationWizardLoading,
    isGerritInstallationWizardAvailable,
    isGerritInstallationWizardLoading,
    hasGithubCloudWizardAccess,
    hasGithubServerWizardAccess,
    isGithubPublicAppInstallationWizardAvailable,
    isGithubPublicAppInstallationWizardLoading,
  } = useUser();

  const isAuthenticated = !!authUser;
  const shouldIdentifyUser = isAuthenticated && !!user && !isAirgappedMode();

  const renderLoginRoutes = () => (
    <>
      <Route path={ROUTES.ERROR} element={<Error />} />
      <Route
        path={ROUTES.SUCCESS}
        element={
          <SuccessPage translationKey="generic-success" className="h-screen" />
        }
      />
      <Route
        path={ROUTES.SIGNIN}
        element={
          <LoginProtectedRoute>
            <Login />
          </LoginProtectedRoute>
        }
      >
        <Route index element={<SigninMethods />} />
        <Route path={ROUTE_PATHS.EMAIL} element={<Email />} />
        <Route path={ROUTE_PATHS.SSO} element={<SSO />} />
        <Route
          path={ROUTE_PATHS.CHECK_YOUR_EMAIL}
          element={<CheckYourEmail />}
        />
        <Route
          path={ROUTE_PATHS.SUCCESS_SIGNIN_EXTERNAL_CLIENT}
          element={
            <SuccessPage translationKey="success-signin-external-client" />
          }
        />
      </Route>
      <Route path={ROUTES.AWARE}>
        <Route
          path={ROUTE_PATHS.AWARE_SUCCESS}
          element={<AwareSuccessPage />}
        />
      </Route>
    </>
  );

  const renderAirgappedLoginRoutes = () => (
    <>
      <Route path={ROUTES.ERROR} element={<Error />} />
      <Route
        path={ROUTES.SUCCESS}
        element={
          <SuccessPage translationKey="generic-success" className="h-screen" />
        }
      />
      <Route path={ROUTES.SIGNIN} element={<Login />}>
        <Route path={ROUTE_PATHS.SSO} element={<SSO />} />
        <Route
          path={ROUTE_PATHS.SUCCESS_SIGNIN_EXTERNAL_CLIENT}
          element={
            <SuccessPage translationKey="success-signin-external-client" />
          }
        />
        <Route
          index
          element={
            <Navigate
              to={`${ROUTE_PATHS.SSO}${window.location.search}`}
              replace
            />
          }
        />
      </Route>
      <Route
        path="/"
        element={
          <Navigate to={`${ROUTES.SIGNIN}${window.location.search}`} replace />
        }
      />
      <Route path={'*'} element={<Error type={ERROR_TYPES.PAGE_NOT_FOUND} />} />
    </>
  );

  const renderGithubWizardRoutesIfNeeded = () => {
    // GithubManifestInstallationDialog (`github/...`) serves Cloud/ST, Server/MT, and Server/ST.
    // GithubPublicAppInstallationDialog (`github-mt/...`) serves Cloud/MT only.
    // We keep the MT route mounted while the cloud flag is loading so a marketplace
    // deep-link doesn't fall through to the 404 wildcard mid-load.
    const isManifestInstallationDialogActive =
      hasGithubCloudWizardAccess ||
      hasGithubServerWizardAccess ||
      isGithubPublicAppInstallationWizardLoading;
    const isPublicAppInstallationDialogActive =
      isGithubPublicAppInstallationWizardAvailable ||
      isGithubPublicAppInstallationWizardLoading;

    if (
      !isManifestInstallationDialogActive &&
      !isPublicAppInstallationDialogActive
    ) {
      return <></>;
    }

    return (
      <>
        {isManifestInstallationDialogActive && (
          <Route
            path={ROUTE_PATHS.GITHUB_INSTALLATION}
            element={
              <InstallationWizardProtectedRoute>
                <GithubManifestInstallationDialog />
              </InstallationWizardProtectedRoute>
            }
          >
            <Route
              path={ROUTE_PATHS.GITHUB_INSTALLATION_STEP_1}
              element={<OrganizationDetails />}
            />
            <Route
              path={ROUTE_PATHS.GITHUB_INSTALLATION_STEP_2}
              element={<ReviewManifest />}
            />
            <Route
              path={ROUTE_PATHS.GITHUB_INSTALLATION_STEP_3}
              element={<InstallApp />}
            />
            <Route
              path={ROUTE_PATHS.GITHUB_INSTALLATION_STEP_4}
              element={<CheckConnection />}
            />
            <Route
              path={ROUTE_PATHS.GITHUB_INSTALLATION_STEP_5}
              element={<RecommendedDisplay />}
            />
            <Route
              path={ROUTE_PATHS.GITHUB_INSTALLATION_STEP_6}
              element={<EnableQodo />}
            />
          </Route>
        )}
        {isPublicAppInstallationDialogActive && (
          <Route
            path={ROUTE_PATHS.GITHUB_MT_INSTALLATION}
            element={
              <InstallationWizardProtectedRoute>
                <GithubPublicAppInstallationDialog />
              </InstallationWizardProtectedRoute>
            }
          >
            <Route
              path={ROUTE_PATHS.GITHUB_MT_INSTALLATION_STEP_1}
              element={<MTInstallGuide />}
            />
            <Route
              path={ROUTE_PATHS.GITHUB_MT_INSTALLATION_STEP_2}
              element={<MTInstallationComplete />}
            />
          </Route>
        )}
      </>
    );
  };

  const renderAzureWizardRoutesIfNeeded = () => {
    if (
      !isAzureInstallationWizardAvailable &&
      !isAzureInstallationWizardLoading
    ) {
      return <></>;
    }

    return (
      <Route
        path={ROUTE_PATHS.AZURE_INSTALLATION}
        element={
          <InstallationWizardProtectedRoute>
            <AzureInstallationDialog />
          </InstallationWizardProtectedRoute>
        }
      >
        <Route
          path={ROUTE_PATHS.AZURE_INSTALLATION_STEP_0}
          element={<AzureChooseMethod />}
        />
        <Route
          path={ROUTE_PATHS.AZURE_INSTALLATION_STEP_1}
          element={<AzureReviewManifest />}
        />
        <Route
          path={ROUTE_PATHS.AZURE_INSTALLATION_STEP_2}
          element={<AzureInstallGuide />}
        />
        <Route
          path={ROUTE_PATHS.AZURE_INSTALLATION_STEP_3}
          element={<AzureConnectRepositories />}
        />
        <Route
          path={ROUTE_PATHS.AZURE_INSTALLATION_STEP_4}
          element={<AzureCheckConnection />}
        />
        <Route
          path={ROUTE_PATHS.AZURE_INSTALLATION_STEP_5}
          element={<AzureEnableQodo />}
        />
      </Route>
    );
  };

  const renderGitlabWizardRoutesIfNeeded = () => {
    // v2 is the single unified flow for every segment, so this route also serves
    // MT-cloud users (who only have the MT-cloud flag) once v2 is enabled. With
    // v2 off, only the self-hosted/ST flag admits them here (MT stays on -mt).
    const isV2MtCloudUser =
      isGitlabWizardV2Enabled && isGitlabMTCloudInstallationWizardAvailable;
    const isGitlabWizardAvailable =
      isGitlabInstallationWizardAvailable || isV2MtCloudUser;
    const isGitlabWizardLoading =
      isGitlabInstallationWizardLoading ||
      isGitlabWizardV2Loading ||
      isGitlabMTCloudInstallationWizardLoading;

    if (!isGitlabWizardAvailable && !isGitlabWizardLoading) {
      return <></>;
    }

    // Wait for the v2 flag before committing to a dialog — mounting v1 first
    // (flag pending) lets its MultiStepDialog redirect to a v1 step, then
    // swapping to v2 renders that v1 step under the wrong provider for a frame.
    const gitlabDialog = isGitlabWizardV2Loading ? (
      <Loader center />
    ) : isGitlabWizardV2Enabled ? (
      <GitlabInstallationDialogV2 />
    ) : (
      <GitlabInstallationDialog />
    );

    // Register only the active wizard's step routes so a deep-link to the
    // inactive wizard's step can't mount under the wrong context provider (v2
    // steps need GitlabInstallationV2Context, v1 steps need
    // GitlabInstallationContext). The catch-all sends any unmatched step segment
    // (a bookmarked step of the now-inactive wizard, or a deep link arriving
    // while the flag is still loading) to the active wizard's first step, so it
    // never falls through to the app-level 404. Same parent path either way, so
    // the URL stays /integrations/gitlab.
    const gitlabStepFallback = (
      <Route
        path="*"
        element={
          <Navigate
            to={{
              pathname: isGitlabWizardV2Enabled
                ? ROUTES.GITLAB_V2_CONNECT
                : ROUTES.GITLAB_INSTALLATION_STEP_1,
              search: currentSearch,
            }}
            replace
          />
        }
      />
    );

    const gitlabStepRoutes = isGitlabWizardV2Enabled ? (
      <>
        <Route
          path={ROUTE_PATHS.GITLAB_V2_CONNECT}
          element={<GitlabV2Connect />}
        />
        <Route
          path={ROUTE_PATHS.GITLAB_V2_SELECT}
          element={<GitlabV2SelectScope />}
        />
        <Route
          path={ROUTE_PATHS.GITLAB_V2_INSTALL}
          element={<GitlabV2InstallResults />}
        />
        {gitlabStepFallback}
      </>
    ) : (
      <>
        <Route
          path={ROUTE_PATHS.GITLAB_INSTALLATION_STEP_1}
          element={<GitlabBeforeYouBegin />}
        />
        <Route
          path={ROUTE_PATHS.GITLAB_INSTALLATION_STEP_2}
          element={<GitlabCreateAppGuide />}
        />
        <Route
          path={ROUTE_PATHS.GITLAB_INSTALLATION_STEP_3}
          element={<GitlabConfigureApp />}
        />
        <Route
          path={ROUTE_PATHS.GITLAB_INSTALLATION_STEP_4}
          element={<GitlabCreateServiceAccountGuide />}
        />
        <Route
          path={ROUTE_PATHS.GITLAB_INSTALLATION_STEP_5}
          element={<GitlabConnectRepositories />}
        />
        <Route
          path={ROUTE_PATHS.GITLAB_INSTALLATION_STEP_6}
          element={<GitlabCheckConnection />}
        />
        <Route
          path={ROUTE_PATHS.GITLAB_INSTALLATION_STEP_7}
          element={<GitlabEnableQodo />}
        />
        <Route
          path={ROUTE_PATHS.GITLAB_INSTALLATION_CONFIGURE_TOKEN}
          element={<GitlabConfigureToken />}
        />
        {gitlabStepFallback}
      </>
    );

    return (
      <Route
        path={ROUTE_PATHS.GITLAB_INSTALLATION}
        element={
          <InstallationWizardProtectedRoute>
            {gitlabDialog}
          </InstallationWizardProtectedRoute>
        }
      >
        {gitlabStepRoutes}
      </Route>
    );
  };

  const renderGitlabMTWizardRoutesIfNeeded = () => {
    if (
      isWizardUnrestricted ||
      (!isGitlabMTCloudInstallationWizardAvailable &&
        !isGitlabMTCloudInstallationWizardLoading)
    ) {
      return <></>;
    }

    // The MT route stays on the v1 MT wizard. v2 is a single unified PAT flow
    // served from /integrations/gitlab for every segment, so it is not mounted
    // here; /integrations/gitlab-mt exists only for v1 MT-cloud users.
    return (
      <Route
        path={ROUTE_PATHS.GITLAB_MT_INSTALLATION}
        element={
          <InstallationWizardProtectedRoute>
            <GitlabMTInstallationDialog />
          </InstallationWizardProtectedRoute>
        }
      >
        <Route
          path={ROUTE_PATHS.GITLAB_INSTALLATION_STEP_1}
          element={<GitlabBeforeYouBegin />}
        />
        <Route
          path={ROUTE_PATHS.GITLAB_INSTALLATION_STEP_2}
          element={<GitlabCreateServiceAccountGuide />}
        />
        <Route
          path={ROUTE_PATHS.GITLAB_INSTALLATION_STEP_3}
          element={<GitlabConfigureMTApp />}
        />
        <Route
          path={ROUTE_PATHS.GITLAB_INSTALLATION_STEP_4}
          element={<GitlabConnectRepositories />}
        />
        <Route
          path={ROUTE_PATHS.GITLAB_INSTALLATION_STEP_5}
          element={<GitlabCheckConnection />}
        />
        <Route
          path={ROUTE_PATHS.GITLAB_INSTALLATION_STEP_6}
          element={<GitlabEnableQodo />}
        />
        <Route
          path={ROUTE_PATHS.GITLAB_INSTALLATION_CONFIGURE_TOKEN}
          element={<GitlabConfigureToken />}
        />
      </Route>
    );
  };

  const renderBitbucketWizardRoutesIfNeeded = () => {
    if (
      !isBitbucketInstallationWizardAvailable &&
      !isBitbucketInstallationWizardLoading
    ) {
      return <></>;
    }

    return (
      <Route
        path={ROUTE_PATHS.BITBUCKET_INSTALLATION}
        element={
          <InstallationWizardProtectedRoute>
            <BitbucketInstallationDialog />
          </InstallationWizardProtectedRoute>
        }
      >
        <Route
          path={ROUTE_PATHS.BITBUCKET_INSTALLATION_STEP_1}
          element={<BitbucketBeforeYouBegin />}
        />
        <Route
          path={ROUTE_PATHS.BITBUCKET_INSTALLATION_STEP_2}
          element={<BitbucketCreateBotUser />}
        />
        <Route
          path={ROUTE_PATHS.BITBUCKET_INSTALLATION_STEP_3}
          element={<BitbucketInstallGuide />}
        />
        <Route
          path={ROUTE_PATHS.BITBUCKET_INSTALLATION_STEP_4}
          element={<BitbucketSelectRepositories />}
        />
        <Route
          path={ROUTE_PATHS.BITBUCKET_INSTALLATION_STEP_5}
          element={<BitbucketCheckConnection />}
        />
        <Route
          path={ROUTE_PATHS.BITBUCKET_INSTALLATION_STEP_6}
          element={<BitbucketEnableQodo />}
        />
      </Route>
    );
  };

  const renderGerritWizardRoutesIfNeeded = () => {
    if (
      !isGerritInstallationWizardAvailable &&
      !isGerritInstallationWizardLoading
    ) {
      return <></>;
    }

    return (
      <Route
        path={ROUTE_PATHS.GERRIT_INSTALLATION}
        element={
          <InstallationWizardProtectedRoute>
            <GerritInstallationDialog />
          </InstallationWizardProtectedRoute>
        }
      >
        <Route
          path={ROUTE_PATHS.GERRIT_INSTALLATION_STEP_1}
          element={<GerritPrerequisites />}
        />
        <Route
          path={ROUTE_PATHS.GERRIT_INSTALLATION_STEP_2}
          element={<GerritConnect />}
        />
        <Route
          path={ROUTE_PATHS.GERRIT_INSTALLATION_STEP_3}
          element={<GerritImportProgress />}
        />
      </Route>
    );
  };

  const renderAppRoutes = () => (
    <>
      <Route
        path={ROUTES.ONBOARDING}
        element={
          <OnboardingWizardProtectedRoute>
            <OnboardingWizardLayout />
          </OnboardingWizardProtectedRoute>
        }
      >
        <Route path={'1'} element={<LinkGitAccounts />} />
      </Route>

      <Route
        path={ROUTES.ROOT}
        element={
          <RootProtectedRoute>
            <MainLayout />
          </RootProtectedRoute>
        }
      >
        <Route index element={<Home />} />

        <Route path={ROUTES.HOME} element={<Home />} />
        <Route
          path={ROUTES.PLANS}
          element={
            <PlansProtectedRoute>
              <Plans />
            </PlansProtectedRoute>
          }
        />

        <Route
          path={ROUTES.MANAGEMENT_REPOSITORIES}
          element={
            <FeatureFlagProtectedRoute
              featureId={FEATURE_FLAGS.GIT_REPO_MANAGEMENT}
            >
              <Repositories />
            </FeatureFlagProtectedRoute>
          }
        >
          <Route
            index
            element={<Navigate to={ROUTE_PATHS.INSTALLATIONS} replace />}
          />
          <Route path={ROUTE_PATHS.INSTALLATIONS} element={null} />
          <Route
            path={ROUTE_PATHS.REPO_RELATIONSHIPS}
            element={
              <FeatureFlagProtectedRoute
                featureId={FEATURE_FLAGS.CROSS_REPO_RELATIONSHIPS_TAB}
                fallbackTo={ROUTES.INSTALLATIONS}
              >
                {null}
              </FeatureFlagProtectedRoute>
            }
          />
          <Route path={ROUTE_PATHS.REPOSITORIES} element={null}>
            <Route
              path={PARAMS.REPOSITORY_ID}
              element={<RepositoriesSidePanel />}
            />
          </Route>
        </Route>
        <Route
          path={`/${ROUTE_PATHS.REPOSITORIES}`}
          element={<Navigate to={ROUTES.REPOSITORIES} replace />}
        />

        <Route
          path={ROUTES.TEAMS_MANAGEMENT}
          element={
            <TeamsProtectedRoute>
              <Teams />
            </TeamsProtectedRoute>
          }
        />

        <Route path={ROUTES.SETTINGS} element={<Settings />} />

        <Route path={ROUTES.ORGANIZATION} element={<Organizations />}>
          <Route
            path={`${ROUTE_PATHS.USERS}/${PARAMS.USER_ID}`}
            element={<UserSidePanel />}
          />
        </Route>

        <Route path={ROUTES.RULES} element={<RulesWrapper />}>
          <Route path={ROUTE_PATHS.NEW} element={<RuleSidePanel />} />
          <Route
            path={`${ROUTE_PATHS.SKILLS}/${PARAMS.SKILL_ID}`}
            element={<SkillSidePanel />}
          />
          <Route path={PARAMS.RULE_ID} element={<RuleSidePanel />} />
        </Route>

        <Route path={ROUTES.FINDINGS} element={<Findings />}>
          <Route path={PARAMS.FINDING_ID} element={<FindingSidePanel />} />
        </Route>

        <Route
          path={ROUTES.WIKI}
          element={
            <WikiCapabilityProtectedRoute>
              <Wiki />
            </WikiCapabilityProtectedRoute>
          }
        />
        <Route
          path={ROUTES.WIKI_REPOSITORY}
          element={
            <WikiCapabilityProtectedRoute>
              <WikiRepositoryView />
            </WikiCapabilityProtectedRoute>
          }
        />

        {/* Nested Suspense with a skeleton fallback so the chunk-download
            window shows an intentional header+content skeleton instead of the
            shared bare "•••" Loader (PER-138 #6). React uses the nearest
            boundary, so this overrides the app-level <Loader center/> for the
            persona routes only; once the chunk resolves, each page's own
            data-phase skeleton / table loading state takes over. */}
        <Route
          path={ROUTES.TEAM_PROFILES}
          element={
            <PersonasProtectedRoute>
              <Suspense fallback={<TeamProfilesRouteFallback />}>
                <TeamProfiles />
              </Suspense>
            </PersonasProtectedRoute>
          }
        />
        <Route
          path={ROUTES.TEAM_PROFILE_VIEW}
          element={
            <PersonasProtectedRoute>
              <Suspense fallback={<TeamProfilesRouteFallback />}>
                <TeamProfileDetail />
              </Suspense>
            </PersonasProtectedRoute>
          }
        />

        <Route
          path={ROUTES.ANALYTICS}
          element={
            <AnalyticsProtectedRoute>
              <AnalyticsPage />
            </AnalyticsProtectedRoute>
          }
        />
        <Route path={ROUTES.INTEGRATIONS} element={<IntegrationsPage />}>
          {renderGithubWizardRoutesIfNeeded()}
          {renderAzureWizardRoutesIfNeeded()}
          {renderGitlabWizardRoutesIfNeeded()}
          {renderGitlabMTWizardRoutesIfNeeded()}
          {renderBitbucketWizardRoutesIfNeeded()}
          {renderGerritWizardRoutesIfNeeded()}
        </Route>
        <Route path={ROUTES.INDEX_MONITOR} element={<IndexingMonitor />} />
        <Route path={ROUTES.CONFIGURATION} element={<ConfigurationPage />} />

        <Route
          path={ROUTES.BILLING}
          element={
            <BillingProtectedRoute>
              <Billing />
            </BillingProtectedRoute>
          }
        >
          <Route
            index
            element={<Navigate to={ROUTE_PATHS.BILLING_ANALYTICS} replace />}
          />
          <Route
            path={ROUTE_PATHS.BILLING_ANALYTICS}
            element={<AnalyticsTab />}
          />
          <Route
            path={ROUTE_PATHS.BILLING_TRANSACTION_HISTORY}
            element={<TransactionHistoryTab />}
          />
          <Route
            path={ROUTE_PATHS.BILLING_MANAGE_SUBSCRIPTION}
            element={
              <ManageSubscriptionProtectedRoute>
                <ManageSubscriptionTab />
              </ManageSubscriptionProtectedRoute>
            }
          />
        </Route>

        {/* Stripe checkout returns to bare `/billing?checkout=…&session_id=…`
            (PLT-3715 / qodo-platform #2409); bridge it to the real billing
            route, preserving the query so usePostCheckoutReturn can read it. */}
        <Route
          path={`/${ROUTE_PATHS.BILLING}`}
          element={
            <Navigate
              to={`${ROUTES.BILLING_ANALYTICS}${currentSearch}`}
              replace
            />
          }
        />

        <Route
          path={ROUTES.API_KEYS}
          element={
            <FeatureFlagProtectedRoute featureId={FEATURE_FLAGS.API_KEYS}>
              <ApiKeys />
            </FeatureFlagProtectedRoute>
          }
        />

        <Route
          path={ROUTES.SANDBOX_CONFIG}
          element={
            <FeatureFlagProtectedRoute featureId={FEATURE_FLAGS.SANDBOX_CONFIG}>
              <SandboxConfiguration />
            </FeatureFlagProtectedRoute>
          }
        />
      </Route>
      <Route path={'*'} element={<Error type={ERROR_TYPES.PAGE_NOT_FOUND} />} />
    </>
  );

  if (shouldIdentifyUser) {
    mixpanelService.identify(user.userId as string);
    mixpanelService.setUserProperties({ email: authUser?.email });

    identifySentryUser(user);
  }

  const renderRoutes = () => {
    if (isAirgappedMode()) {
      return renderAirgappedLoginRoutes();
    }

    return (
      <>
        {renderLoginRoutes()}
        <Route
          path="integrations/bitbucket/oauth"
          element={<OAuthRedirectPage />}
        />
        <Route
          path="integrations/bitbucket/oauth-complete"
          element={<OAuthCallbackReceiver />}
        />
        <Route
          path={ROUTES.GITHUB_MT_MARKETPLACE_CALLBACK}
          element={<GithubMarketplaceWizardProtectedRoute />}
        />
        {isAuthenticated ? (
          renderAppRoutes()
        ) : (
          // Unauthenticated: no app routes are mounted, so `/` (and any other
          // path) would match nothing and render blank. Send them to /signin —
          // the login routes above still win by specificity. Mirrors the
          // airgapped branch's `/` -> signin redirect.
          <Route
            path="*"
            element={
              <Navigate to={`${ROUTES.SIGNIN}${currentSearch}`} replace />
            }
          />
        )}
      </>
    );
  };

  return (
    <SidebarProvider>
      <Suspense fallback={<Loader center />}>
        <RouterRoutes>{renderRoutes()}</RouterRoutes>
      </Suspense>
    </SidebarProvider>
  );
};
